import { ILinkedServiceTypeMetadata } from 'app/extensibility/metadatas/resource-type/linked-service-type-metadata';

import { prestoAuthTypes } from 'app/model/linkedservice/presto.linkedservice';
import { enableSslWithTUAARadioSwitch } from 'app/registries/linked-service/linked-service-shared-fields';
import { format } from 'app/shared/utils/string.util';
import { ControlMetadata } from 'app/extensibility/metadatas/control/control-metadata';
import { GlobalVariables } from 'app/services/globals';
import { driverVersionVersionOptions, driverVersions } from '../consts/tabular-consts';
import { adfModelTypeMap } from '../consts/adf-model-type-map-const';
import { CommonResourceKind, DataSourceOptionKind } from '@powerquery/mashup-editor-client';

const host: ControlMetadata = {
    type: 'textbox',
    bind: 'host',
    label: stringResources.ModelName_Host,
    required: true,
    placeholder: stringResources.Host_Placeholder,
    validators: [
        { type: 'required', errorMessage: '' }
    ],
    isLabelVertical: true
};
const server: ControlMetadata = {
    type: 'textbox',
    bind: 'serverVersion',
    label: stringResources.ModelName_ServerVersion,
    required: true,
    placeholder: stringResources.Presto_LinkedService_ServerVersion_Placeholder,
    validators: [
        { type: 'required', errorMessage: '' }
    ],
    isLabelVertical: true
};
const catalog: ControlMetadata = {
    type: 'textbox',
    bind: 'catalog',
    label: stringResources.ModelName_Catalog,
    required: true,
    validators: [
        { type: 'required', errorMessage: '' }
    ],
    isLabelVertical: true
};
function port(portNumber: number): ControlMetadata {
    return {
        type: 'textbox-number',
        bind: 'port',
        label: stringResources.ModelName_Port,
        isLabelVertical: true,
        inputType: 'number',
        defaultValue: portNumber,
        min: 0
    }
}
const auth: ControlMetadata = {
    type: 'select-switch',
    bind: 'authenticationType',
    label: stringResources.ModelName_AuthenticationType,
    options: [
        {
            label: stringResources.Anonymous_DisplayText,
            value: prestoAuthTypes.Anonymous
        },
        {
            label: stringResources.AuthenticationType_LDAP,
            value: prestoAuthTypes.PrestoLDAP
        }
    ],
    required: true,
    validators: [
        { type: 'required', errorMessage: '' }
    ],
    isLabelVertical: true,
    defaultValue: prestoAuthTypes.Anonymous,
    cases: [
        {
            caseValue: prestoAuthTypes.PrestoLDAP,
            children: [
                {
                    type: 'textbox',
                    bind: 'username',
                    label: stringResources.ModelName_UserName,
                    required: true,
                    validators: [
                        { type: 'required', errorMessage: '' }
                    ],
                    isLabelVertical: true
                },
                {
                    type: 'dynamic-akv-switcher',
                    bind: 'password',
                    label: stringResources.ModelName_Password,
                    child: {
                        type: 'password',
                        label: stringResources.ModelName_Password,
                        required: true,
                        isLabelVertical: true
                    },
                    validators: [
                        { type: 'required', errorMessage: '' }
                    ],
                    description: format(stringResources.AzureKeyVaultSecretName_Tooltip_Template,
                        format(stringResources.ModelName_LinkedServiceTemplate, stringResources.Presto_DisplayText),
                        stringResources.ModelName_Password)
                }
            ]
        }
    ]
};
const time: ControlMetadata = {
    type: 'textbox',
    bind: 'timeZoneID',
    label: stringResources.Timezone,
    isLabelVertical: true
};
const oldVersionMetadata: ControlMetadata[] = [
    host,
    server,
    catalog,
    port(8080),
    auth,
    enableSslWithTUAARadioSwitch,
    time
];

const enableSslSimplified: ControlMetadata = {
    type: 'radio-switch',
    nested: 1,
    bind: 'enableSsl',
    label: stringResources.ModelName_EnableSSL,
    isLabelVertical: true,
    options: [
        {
            label: stringResources.LinkedService_EnableSSL,
            value: true
        },
        {
            label: stringResources.LinkedService_DisableSSL,
            value: false
        }
    ],
    defaultValue: true,
    cases: [
        {
            caseValue: true,
            children: [
                {
                    type: 'radio',
                    bind: 'enableServerCertificateValidation',
                    label: stringResources.ModelName_EnableServerCertificateValidation,
                    isLabelVertical: true,
                    options: [
                        {
                            label: stringResources.Enable,
                            value: true
                        },
                        {
                            label: stringResources.Disable,
                            value: false
                        }
                    ],
                    defaultValue: true
                }
            ]
        }
    ],
    sharedControls: [
        {
            caseValues: [true],
            controls: [port(8443)]
        },
        {
            caseValues: [false],
            controls: [port(8080)]
        }
    ]
};

export const prestoMetadata: ILinkedServiceTypeMetadata = {
    type: adfModelTypeMap.presto.linkedServiceType,
    displayName: stringResources.Presto_DisplayText,
    linkedServiceKind: 'table',
    icon: {
        className: 'svg-i-100x100-presto'
    },
    isFirstClass: true,
    category: ['Database'],
    isDeprecated: !GlobalVariables.showLegacy ? '{ version !== "v2" }' : false,
    showWarningMessage: '{ version !== "v2" }',
    warningMessage: format(
        stringResources.Connector_Legacy_IsNotSupportedWarning_alt5,
        stringResources.VersionLabel.toLocaleLowerCase()
    ),
    warningMessageHyperlink: 'https://learn.microsoft.com/azure/data-factory/connector-presto',
    supportDatasetV2: true,
    copySinkTypes: [],
    copySourceTypes: [adfModelTypeMap.presto.copySourceType],
    SHIRVersion: {
        v2: '5.51.00'
    },
    supportedInTrident: true,
    supportAutoFillCopilotInput: true,
    isKnownInPipelineContext: true,
    supportCopyJobSource: true,
    supportMultipleIncrementalColumns: true,
    pqoDataSourceOption: {
        kind: DataSourceOptionKind.Extension,
        resourceKind: 'Presto' as CommonResourceKind
    },
    supportOPDG: GlobalVariables.enableOPDG,
    controls: [
        {
            type: 'radio-switch',
            bind: 'version',
            label: stringResources.VersionLabel,
            isLabelVertical: true,
            required: true,
            options: driverVersionVersionOptions,
            defaultValue: driverVersions.v2,
            hide: GlobalVariables.showLegacy ? false : '{version === "v2" }',
            cases: [
                {
                    caseValue: driverVersions.v1,
                    children: [
                        {
                            type: 'hint',
                            hintMode: 'warning',
                            description: format(
                                stringResources.Connector_Legacy_IsNotSupportedWarning_alt6,
                                stringResources.VersionLabel.toLocaleLowerCase()),
                            link: 'https://learn.microsoft.com/azure/data-factory/connector-presto'
                        },
                        ...oldVersionMetadata
                    ]
                },
                {
                    caseValue: driverVersions.v2,
                    children: [
                        host,
                        catalog,
                        auth,
                        enableSslSimplified,
                        time
                    ]
                }
            ]
        },
        ...[
            host,
            catalog,
            auth,
            enableSslSimplified,
            time
        ].map((control) => {
            return {
                ...control,
                hide: !GlobalVariables.showLegacy ? '{version !== "v2" }' : true
            };
        })
    ]
};
